import { Abac } from '@gunosy/media-admin-components'
import React from 'react'
import {
  IoHomeOutline,
  IoCheckmarkCircleOutline,
  IoFilmOutline,
  IoServerOutline,
  IoReceiptOutline,
  IoBusinessOutline,
  IoNewspaperOutline,
  IoCashOutline,
  IoSettingsOutline,
  IoMegaphoneOutline,
  IoPeopleOutline,
  IoLinkOutline,
  IoMedalOutline,
} from 'react-icons/io5'
import * as Resource from '../../const/resources'
import Role from '../../const/role'
import { UserState } from '../../store/loggedInUser'
import { fmtYM_HYPHEN } from '../../utils'
import { getFirstDayOfLastMonth } from '../../utils/time'

export interface NavItem {
  id: string
  icon: React.ComponentType<any>
  label: string
  to?: string
  content?: NavItem[]
  resource?: Abac.Resource
  externalLink?: boolean
  hide?: (isInhouse: boolean, loggedInUser: UserState) => boolean // 権限あるけどメニューには出さない
}
export interface NavSection {
  id: string
  title: string
  items: NavItem[]
}

export const Menu: NavSection[] = [
  {
    id: 'reports',
    title: 'レポート',
    items: [
      {
        id: 'home',
        icon: IoHomeOutline,
        label: 'ホーム',
        to: '#/reports/dashboard',
        resource: Resource.SummaryStats,
      },
      {
        id: 'publisher-ranking',
        icon: IoMedalOutline,
        label: 'パブリッシャーランキング',
        to: '#/reports/publisher-ranking',
        resource: Resource.PublisherRank,
        hide: (isInhouse: boolean) => !isInhouse,
      },
      {
        id: 'articles',
        icon: IoNewspaperOutline,
        label: '記事レポート',
        to: '#/reports/articles',
        resource: Resource.ArticleStats,
      },
      {
        id: 'video',
        icon: IoFilmOutline,
        label: '動画レポート',
        to: '#/reports/video',
        resource: Resource.VideoStats,
      },
    ],
  },
  {
    id: 'publisher-management',
    title: 'パブリッシャー管理',
    items: [
      {
        id: 'agreements',
        icon: IoCheckmarkCircleOutline,
        label: '契約管理',
        to: '#/managements/agreements',
        resource: Resource.Agreement,
        hide: (isInhouse: boolean) => !isInhouse,
      },
      {
        id: 'publishers',
        icon: IoServerOutline,
        label: 'パブリッシャー管理',
        to: '#/managements/publishers',
        resource: Resource.Publisher,
        hide: (isInhouse: boolean) => !isInhouse,
      },
      {
        id: 'feeds',
        icon: IoReceiptOutline,
        label: 'フィード管理',
        to: '#/managements/feeds',
        resource: Resource.Feed,
      },
      {
        id: 'bank-accounts',
        icon: IoBusinessOutline,
        label: '銀行口座管理',
        to: '#/bank-accounts',
        resource: Resource.BankAccount,
      },
    ],
  },
  {
    id: 'article-management',
    title: '記事管理',
    items: [
      {
        id: 'articles',
        icon: IoNewspaperOutline,
        label: '記事管理',
        to: '#/managements/articles',
        resource: Resource.MediumArticle,
      },
    ],
  },
  {
    // for 社内向け
    id: 'payments-inhouse',
    title: '支払',
    items: [
      {
        id: 'by-agreement',
        icon: IoCashOutline,
        label: '契約別支払一覧',
        to: '#/histories/payments-by-agreement',
        resource: Resource.MonthlyPayment,
        hide: (isInhouse: boolean) => !isInhouse,
      },
      {
        id: 'by-publisher',
        icon: IoCashOutline,
        label: 'パブリッシャー別支払一覧',
        to: '#/histories/payments-by-publisher',
        resource: Resource.MonthlyPayment,
        hide: (isInhouse: boolean, loggedInUser: UserState) =>
          !loggedInUser.roles.includes(Role.Admin) &&
          !loggedInUser.roles.includes(Role.Insight_Admin) &&
          !loggedInUser.roles.includes(Role.Insight_Editor),
      },
    ],
  },
  {
    // for パブリッシャー向け
    id: 'payments-publisher',
    title: '支払',
    items: [
      {
        id: 'monthly-list',
        icon: IoCashOutline,
        label: '支払一覧',
        to: `#/histories/payments-for-publishers/month=${fmtYM_HYPHEN(getFirstDayOfLastMonth())}`,
        resource: Resource.MonthlyPayment,
        hide: (isInhouse: boolean, loggedInUser: UserState) =>
          !loggedInUser.roles.includes(Role.Insight_PublisherAdmin) &&
          !loggedInUser.roles.includes(Role.Insight_PublisherEditor),
      },
    ],
  },
  {
    id: 'adjustments',
    title: '調整',
    items: [
      {
        id: 'dividend',
        icon: IoSettingsOutline,
        label: '調整金分配',
        to: '#/adjustments/dividend',
        resource: Resource.Dividend,
      },
    ],
  },
  {
    // for 社内向け
    id: 'announcements',
    title: 'お知らせ管理',
    items: [
      {
        id: 'management',
        icon: IoMegaphoneOutline,
        label: 'お知らせ管理',
        to: '#/announcements',
        resource: Resource.Announce,
        hide: (isInhouse: boolean) => !isInhouse,
      },
    ],
  },
  {
    // NOTE: IAMにも同様の頁があるが、'insight:publisher_editor' は agreementと紐付けないといけないのでこちらで管理する
    // NOTE: publisher側はこちらしか使わない。
    // for パブリッシャー向け
    id: 'user-management',
    title: 'ユーザー管理',
    items: [
      {
        id: 'users',
        icon: IoPeopleOutline,
        label: 'ユーザー管理',
        to: '#/users',
        resource: Resource.User,
        hide: (isInhouse: boolean, loggedInUser: UserState) =>
          !loggedInUser.roles.includes(Role.Insight_PublisherAdmin),
      },
    ],
  },
  {
    id: 'links',
    title: 'リンク',
    items: [
      {
        id: 'faq',
        icon: IoLinkOutline,
        label: 'Feedや収益などに関するFAQ',
        to: 'https://support.gunosy.link/hc/ja/categories/4808592419471',
        externalLink: true,
      },
      {
        id: 'feed-spec',
        icon: IoLinkOutline,
        label: 'GunosyFeed仕様書',
        to: 'https://support.gunosy.link/hc/ja/articles/5533509025935',
        externalLink: true,
      },
      {
        id: 'tutorial',
        icon: IoLinkOutline,
        label: '管理画面説明書',
        to: '/gpc/guide/gunosy-publisher-center-tutorial.pdf',
        externalLink: true,
      },
      {
        id: 'service-terms',
        icon: IoLinkOutline,
        label: 'サービス連携規約',
        to: 'https://support.gunosy.link/hc/ja/articles/8038248964239',
        externalLink: true,
      },
      {
        id: 'content-guidelines',
        icon: IoLinkOutline,
        label: 'コンテンツ掲載ガイドライン',
        to: 'https://support.gunosy.link/hc/ja/sections/5585000600335',
        externalLink: true,
      },
    ],
  },
]
